<template>
  <div class="breadcrumb" v-if="parents">
    <template v-if="result?.item?.parent?.path?.endsWith('/home/partner')">
      <a href="/">Home</a>
      <a v-if="result?.item?.partnerTypes?.value" :href="partnerUrl">
        {{ getPartnerDisplayname(result.item.partnerTypes.value) }}
      </a>
      <span>{{ pageTitle }}</span>
    </template>
    <template v-else>
      <template v-for="item in parents">
        <a v-if="item.title" :key="item.url" :href="item.url">{{ item.title }}</a>
      </template>
      <span>{{ pageTitle }}</span>
    </template>
  </div>
  <structured-data-breadcrumb-list
    :pageTitle="pageTitle"
    :sitecoreApiHost="sitecoreApiHost"
    :partnerUrl="partnerUrl"
    :partnerDisplayName="partnerDisplayName"
    :parents="parents"
  />
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { BreadcrumbQuery } from './CIS-Breadcrumb.graphql';
import StructuredDataBreadcrumbList from '../../StructuredDataBreadcrumbList.vue';
import config from '../../temp/config';

export default {
  name: 'CIS-Breadcrumb',
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  data: function () {
    return {
      partnerUrl: '',
      partnerDisplayName: '',
      sitecoreApiHost: config.sitecoreApiHost
    };
  },
  components: {
    StructuredDataBreadcrumbList
  },
  setup(props) {
    const instance = getCurrentInstance();

    const variables = () => {
      return {
        contextItem:
          instance?.appContext?.config?.globalProperties?.$jss?.sitecoreContext()?.itemId ??
          '{00000000-0000-0000-0000-000000000000}' // default value to prevent GraphQL errors
      };
    };

    const { result, loading, error } = useQuery(BreadcrumbQuery, variables());

    return {
      result,
      loading,
      error
    };
  },
  computed: {
    isEditing() {
      return this.$jss.sitecoreContext().pageEditing;
    },
    parents() {
      const parents = [];
      const homePath = '/sitecore/content/cisecurity/ngen/home';
      const addParent = (parent) => {
        if (!parent) {
          return false;
        }

        if (parent.path === homePath) {
          parents.push({
            title: 'Home',
            url: parent?.url
          });
          return true;
        } else if (parent.path && parent.path.startsWith(homePath)) {
          parents.push({
            title: parent?.pageTitle?.value,
            url: parent?.url
          });
          return true;
        } else {
          return false;
        }
      };

      // This can only work 4 levels deep because of limits in the GraphQL query
      // Found one path nested 4 levels deep from the home page, but none with 5:
      //   /sitecore/content/cisecurity/ngen/home/cis-securesuite/pricing-and-categories/product-vendor/cis-benchmark-assessment
      if (addParent(this.result?.item?.parent)) {
        if (addParent(this.result?.item?.parent?.parent)) {
          if (addParent(this.result?.item?.parent?.parent?.parent)) {
            addParent(this.result?.item?.parent?.parent?.parent?.parent);
          }
        }
      }

      if (parents.length > 0) {
        return parents.reverse();
      } else if (this.isEditing) {
        return [
          { title: 'Home', url: '/' },
          { title: 'Level 1', url: '/level-1' },
          { title: 'Level 2', url: '/level-1/level-2' }
        ];
      } else {
        return undefined;
      }
    },
    pageTitle() {
      return this.$jss?.routeData()?.fields?.pageTitle?.value || 'Example Page';
    }
  },
  methods: {
    getPartnerDisplayname(value) {
      let itemID = '';

      if (value) {
        itemID = value
          .slice(0, value.indexOf('|'))
          .replaceAll(/-/g, '')
          .replaceAll('{', '')
          .replaceAll('}', '');
      }

      const partnerName = this.result?.partners?.children?.filter((c) => {
        return itemID.includes(c.id);
      });

      // Create url based on display name

      // Service Members
      if (partnerName[0].displayName === 'CIS Security Benchmarks Security Service Members')
        this.partnerUrl = '/partners-services';

      // Vendor Members Uncertified or Certified
      if (
        partnerName[0].displayName ===
          'CIS Security Benchmarks Security Vendor Members Uncertified' ||
        partnerName[0].displayName === 'CIS Security Benchmarks Security Vendor Members Certified'
      )
        this.partnerUrl = '/partners-vendor';

      // End User Members
      if (partnerName[0].displayName === 'CIS Security Benchmarks End User Members')
        this.partnerUrl = '/cis-securesuite/members/partners-enduser';

      // Academic Institution Members
      if (partnerName[0].displayName === 'CIS Security Benchmarks Academic Institution Members')
        this.partnerUrl = '/cis-securesuite/members/partners-edu';

      // Government Agency Members
      if (partnerName[0].displayName === 'CIS Security Benchmarks Government Agency Members')
        this.partnerUrl = '/cis-securesuite/members/partners-gov';

      // CIS Controls Supporters
      if (partnerName[0].displayName === 'CIS Controls Supporters')
        this.partnerUrl = '/cis-controls-supporters/partners-control';

      // CIS Controls Accreditation
      if (partnerName[0].displayName === 'CIS Controls Accredited')
        this.partnerUrl =
          '/cis-securesuite/pricing-and-categories/services-and-consulting/cis-controls-accreditation/controls-accreditation';

      this.partnerDisplayName = partnerName[0].displayName;

      return partnerName[0].displayName;
    }
  }
};
</script>

<style lang="scss" scoped>
.breadcrumb {
  margin: 0 auto;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;
  background-color: transparent;
  border-radius: 0;
  color: $gray;
  position: relative;
  @include padding-x-thin;

  a {
    @include font-styles('Arial Narrow', 1.4rem, 200, normal, 1.7rem, 0, inherit);
    position: relative;
    padding-right: 1.7rem;
    margin-right: 0.9rem;
    opacity: 0.5;

    &:hover {
      text-decoration: underline;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0.6rem;
      right: 0.2rem;
      z-index: 1;
      display: inline-block;
      width: 1rem;
      height: 1rem;
      border-top: 0.1rem solid $gray;
      border-right: 0.1rem solid $gray;
      transform: rotateZ(45deg);
      opacity: 0.5;
    }
  }

  span {
    @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0, inherit);
  }

  &:after {
    content: '';
    position: sticky;
    width: 15rem;
    height: 3rem;
    padding: 0;
    margin: -1rem -1.5rem;
    display: inline-block;
    bottom: -1rem;
    right: -1.5rem;
    left: auto;
    background: linear-gradient(-90deg, white, transparent);
  }
}
</style>
